import type { FC } from 'react';
import { Callout } from '@/components/ui/callout';
import { Card } from '@/components/ui/card/Card';
import { SectionLabel } from '@/components/ui/section-label';
import { Spinner } from '@/components/ui/spinner';
import { useTranslation } from '@/hooks/useTranslation';
import type { TranslationParams } from '@/context/LanguageContext';
import type { MLSignalsWindow } from '@/features/machine-learning/type/MLTypes';
import { formatNumber, plural } from '@/features/machine-learning/lib/wearFormat';

interface MLRulHeroCardProps {
  status: 'idle' | 'running' | 'completed' | 'failed';
  jobId: string | null;
  toolId: string | null;
  machineId: number | null;
  machineName: string | null;
  processId: number | null;
  processName: string | null;
  rulSeconds: number | null;
  warning: string | null;
  /** What the prediction read; null before a run or on an old backend. */
  signalsWindow?: MLSignalsWindow | null;
  errorMessage: string | null;
}

const pad = (value: number) => String(value).padStart(2, '0');

/** HH:MM:SS in the browser's time zone, with the date first when it is not today. */
function windowTime(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  const time = `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  if (date.toDateString() === now.toDateString()) return time;
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${time}`;
}

interface SignalsWindowLineProps {
  signalsWindow: MLSignalsWindow;
}

/** "Part P · N passes · start → end · Tool volume V cm³" under the remaining life. */
const SignalsWindowLine: FC<SignalsWindowLineProps> = ({ signalsWindow }) => {
  const { t, language } = useTranslation();
  const { partId, passCount, start, end, volumeRemoved } = signalsWindow;
  const items: Array<{ key: string; content: string }> = [];
  if (partId) items.push({ key: 'part', content: t('mlRul.result.window.part', { part: partId }) });
  if (passCount !== null) {
    items.push({ key: 'passes', content: plural(t, 'mlRul.result.window.passes', passCount) });
  }
  if (start && end) {
    items.push({ key: 'time', content: `${windowTime(start)} → ${windowTime(end)}` });
  }
  if (volumeRemoved !== null) {
    items.push({
      key: 'volume',
      content: t('mlRul.result.window.toolVolume', {
        volume: formatNumber(language, volumeRemoved, 2),
      }),
    });
  }
  if (items.length === 0) return null;

  return (
    <p
      className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground"
      data-testid="signals-window-line"
    >
      {items.map((item, index) => (
        <span key={item.key} className="flex items-center gap-2">
          {index > 0 && <span aria-hidden>·</span>}
          <span className="tabular-nums">{item.content}</span>
        </span>
      ))}
    </p>
  );
};

function humanReadable(
  rulSeconds: number,
  t: (key: string, params?: TranslationParams) => string,
): string {
  if (rulSeconds < 60) {
    return t('mlRul.result.humanReadableSeconds', { seconds: rulSeconds });
  }
  if (rulSeconds >= 86400) {
    const days = Math.floor(rulSeconds / 86400);
    const hours = Math.floor((rulSeconds % 86400) / 3600);
    return t('mlRul.result.humanReadableDays', { days, hours });
  }
  const hours = Math.floor(rulSeconds / 3600);
  const minutes = Math.floor((rulSeconds % 3600) / 60);
  return t('mlRul.result.humanReadable', { hours, minutes });
}

export const MLRulHeroCard: FC<MLRulHeroCardProps> = ({
  status,
  jobId,
  toolId,
  machineId,
  machineName,
  processId,
  processName,
  rulSeconds,
  warning,
  signalsWindow = null,
  errorMessage,
}) => {
  const { t } = useTranslation();

  function renderBadge(label: string) {
    return <SectionLabel>{label}</SectionLabel>;
  }

  if (status === 'idle') {
    return (
      <Card padding="lg" className="flex flex-col gap-4">
        {renderBadge(t('mlRul.result.badgeIdle'))}
        <div>
          <p className="section-title">{t('mlRul.result.idleTitle')}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t('mlRul.result.idleDescription')}</p>
        </div>
      </Card>
    );
  }

  if (status === 'running') {
    return (
      <Card padding="lg" className="flex flex-col gap-4">
        {renderBadge(t('mlRul.result.badgeRunning'))}
        <div className="flex items-center gap-3">
          <Spinner className="size-5 text-primary" />
          <p className="text-sm text-muted-foreground">
            {t('mlRul.result.runningOnTool', { toolId: toolId ?? '' })}
          </p>
        </div>
      </Card>
    );
  }

  if (status === 'failed') {
    return (
      <Card padding="lg" className="flex flex-col gap-4">
        {renderBadge(t('mlRul.result.badgeFailed'))}
        <Callout tone="error" title={t('mlRul.result.errorTitle')} data-testid="error-container">
          {errorMessage}
        </Callout>
      </Card>
    );
  }

  return (
    <Card padding="lg" className="flex flex-col gap-4">
      {renderBadge(t('mlRul.result.badgeCompleted'))}

      {warning && (
        <Callout tone="warning" data-testid="warning-banner">
          {warning}
        </Callout>
      )}

      {rulSeconds !== null && (
        <div className="flex flex-col gap-1">
          <p className="eyebrow">{t('mlRul.result.rulLabel')}</p>
          <div className="flex items-end gap-2">
            <span
              className="text-7xl font-extrabold tracking-tight tabular-nums text-foreground"
              data-testid="rul-value"
            >
              {rulSeconds}
            </span>
            <span className="mb-2 text-lg text-muted-foreground">
              {t('mlRul.result.secondsUnit')}
            </span>
          </div>
          <p className="text-sm text-muted-foreground">{humanReadable(rulSeconds, t)}</p>
          {signalsWindow && <SignalsWindowLine signalsWindow={signalsWindow} />}
        </div>
      )}

      <dl className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
        <div>
          <dt className="eyebrow">
            {t('mlRul.result.contextChipMachine')}
          </dt>
          <dd className="font-mono text-foreground">{machineName ?? machineId}</dd>
        </div>
        <div>
          <dt className="eyebrow">
            {t('mlRul.result.contextChipProcess')}
          </dt>
          <dd className="font-mono text-foreground">{processName ?? processId}</dd>
        </div>
        <div>
          <dt className="eyebrow">
            {t('mlRul.result.contextChipTool')}
          </dt>
          <dd className="font-mono text-foreground">{toolId}</dd>
        </div>
        <div>
          <dt className="eyebrow">
            {t('mlRul.result.contextChipJob')}
          </dt>
          <dd className="font-mono text-foreground">{jobId}</dd>
        </div>
      </dl>
    </Card>
  );
};
